import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiFehler, hole, schicke } from '../lib/api'
import { datumHeute, datumKurz } from '../lib/datum'
import { centsAusEingabe, centsFormatiert, centsMitVorzeichen } from '../lib/geld'
import { useDarf, useSitzung } from '../session/SessionProvider'
import { useFab } from '../shell/FabSlot'
import { mitgliedsFarbe } from '../session/logic'
import {
  Avatar,
  Button,
  Card,
  Chip,
  Empty,
  ListItem,
  Select,
  Sheet,
  TextField,
  useSnackbar,
  useConfirm,
} from '../ui'

interface Konto {
  id: string
  memberId: string
  displayName: string
  currency: string
  balanceCents: number
  recurringAmountCents: number | null
  recurringInterval: string | null
  nextPayoutOn: string | null
}

interface Buchung {
  id: string
  amountCents: number
  kind: string
  note: string | null
  occurredOn: string
  reversesTransactionId: string | null
}

const ART_NAME: Record<string, string> = {
  allowance: 'Taschengeld',
  bonus: 'Bonus',
  spend: 'Ausgabe',
  correction: 'Korrektur',
}

const TAKT_NAME: Record<string, string> = { weekly: 'wöchentlich', monthly: 'monatlich' }

interface NeueBuchung {
  betrag: string
  kind: 'allowance' | 'bonus' | 'spend'
  note: string
  occurredOn: string
}

/**
 * Taschengeld — ein Konto je Mensch, darunter der Auszug.
 *
 * ⚠️ Hier wird NICHTS gerechnet. Der Saldo kommt fertig vom Server; eine
 * Zwischensumme im Browser wäre die zweite Wahrheit, die als Erstes falsch
 * wird.
 */
export function Taschengeld() {
  const qc = useQueryClient()
  const { abfrage } = useSitzung()
  const zeigeMeldung = useSnackbar()
  const bestaetige = useConfirm()
  const preview = Boolean(abfrage.data && !abfrage.data.setupRequired && abfrage.data.preview)
  const darfBuchen = useDarf('allowance.book') && !preview
  const heute = datumHeute()

  const [gewaehlt, setGewaehlt] = useState<string | null>(null)
  const [blattOffen, setBlattOffen] = useState(false)
  const [formular, setFormular] = useState<NeueBuchung>({
    betrag: '',
    kind: 'allowance',
    note: '',
    occurredOn: heute,
  })
  const [fehler, setFehler] = useState<string | null>(null)

  const uebersicht = useQuery({
    queryKey: ['allowance', 'list'],
    queryFn: () => hole<{ accounts: Konto[] }>('/api/allowance'),
  })
  const konten = uebersicht.data?.accounts ?? []
  const aktivId =
    (gewaehlt && konten.some((k) => k.memberId === gewaehlt) ? gewaehlt : null) ??
    konten[0]?.memberId ??
    null

  const auszug = useQuery({
    queryKey: ['allowance', 'konto', aktivId],
    queryFn: () =>
      hole<{ account: Konto; transactions: Buchung[] }>(`/api/allowance/${aktivId ?? ''}`),
    enabled: aktivId !== null,
  })

  const aktualisiere = async () => {
    await qc.invalidateQueries({ queryKey: ['allowance'] })
  }

  const buchen = useMutation({
    mutationFn: async () => {
      const cents = centsAusEingabe(formular.betrag)
      if (cents === null || cents === 0)
        throw new ApiFehler('validation', 'Bitte einen Betrag angeben.', 0)
      // ⚠️ Das Vorzeichen setzt die ART, nicht die Eingabe: wer „Ausgabe"
      // wählt und 5 tippt, meint −5. Sonst müsste jede Person daran denken,
      // und wer es vergisst, erhöht seinen Saldo.
      const betrag = formular.kind === 'spend' ? -Math.abs(cents) : Math.abs(cents)
      return schicke<{ transaction: Buchung }>('POST', `/api/allowance/${aktivId ?? ''}/book`, {
        amountCents: betrag,
        kind: formular.kind,
        note: formular.note.trim() === '' ? null : formular.note.trim(),
        occurredOn: formular.occurredOn,
      })
    },
    onSuccess: async () => {
      setBlattOffen(false)
      setFormular({ betrag: '', kind: 'allowance', note: '', occurredOn: heute })
      setFehler(null)
      await aktualisiere()
      zeigeMeldung('Gebucht.')
    },
    onError: (e: ApiFehler) => setFehler(e.message),
  })

  const stornieren = useMutation({
    mutationFn: (id: string) =>
      schicke<{ transaction: Buchung }>('POST', `/api/allowance/tx/${id}/reverse`),
    onSuccess: async () => {
      await aktualisiere()
      zeigeMeldung('Storniert — die Gegenbuchung steht im Auszug.')
    },
    onError: (e: ApiFehler) => {
      zeigeMeldung(e.message)
    },
  })

  useFab(
    darfBuchen && aktivId !== null
      ? {
          icon: 'plus',
          label: 'Buchen',
          onClick: () => {
            setFehler(null)
            setBlattOffen(true)
          },
        }
      : null,
  )

  const konto = auszug.data?.account
  const zeilen = auszug.data?.transactions ?? []
  const storniert = new Set(zeilen.map((z) => z.reversesTransactionId).filter(Boolean))

  return (
    <>
      <div className="seitenkopf">
        <div>
          <h1>Taschengeld</h1>
          <p>Saldo und Auszug — jede Buchung bleibt stehen.</p>
        </div>
      </div>

      {uebersicht.isPending ? <p className="unterzeile">Wird geladen …</p> : null}

      {!uebersicht.isPending && konten.length === 0 ? (
        <Empty
          titel="Noch kein Konto"
          text="Ein Erwachsener legt in der Familienverwaltung ein Taschengeldkonto an."
        />
      ) : null}

      {konten.length > 1 ? (
        <div className="chips">
          {konten.map((k) => (
            <Chip
              key={k.memberId}
              gewaehlt={k.memberId === aktivId}
              onClick={() => setGewaehlt(k.memberId)}
            >
              <Avatar name={k.displayName} farbe={mitgliedsFarbe(null, k.memberId)} klein />
              {k.displayName}
            </Chip>
          ))}
        </div>
      ) : null}

      {konto ? (
        <Card titel={konten.length > 1 ? konto.displayName : undefined}>
          <p
            className="saldo"
            aria-label={`Saldo ${centsFormatiert(konto.balanceCents, konto.currency)}`}
          >
            {centsFormatiert(konto.balanceCents, konto.currency)}
          </p>
          {konto.recurringAmountCents !== null && konto.recurringInterval !== null ? (
            <p className="unterzeile">
              {centsFormatiert(konto.recurringAmountCents, konto.currency)}{' '}
              {TAKT_NAME[konto.recurringInterval] ?? konto.recurringInterval}
              {konto.nextPayoutOn ? `, nächste Zahlung ${datumKurz(konto.nextPayoutOn)}` : ''}
            </p>
          ) : (
            <p className="unterzeile">Kein fester Takt eingestellt.</p>
          )}
        </Card>
      ) : null}

      {konto ? (
        <section aria-labelledby="auszug-titel">
          <h2 id="auszug-titel">Auszug</h2>
          {zeilen.length === 0 ? (
            <Empty
              titel="Noch nichts gebucht"
              text="Der Auszug füllt sich mit der ersten Buchung."
            />
          ) : (
            <ul className="liste liste--gruppe">
              {[...zeilen].reverse().map((z) => (
                <ListItem
                  key={z.id}
                  titel={
                    <span className={z.amountCents < 0 ? 'betrag betrag--minus' : 'betrag'}>
                      {centsMitVorzeichen(z.amountCents, konto.currency)}
                    </span>
                  }
                  unterzeile={`${datumKurz(z.occurredOn)} · ${ART_NAME[z.kind] ?? z.kind}${
                    z.note ? ` · ${z.note}` : ''
                  }`}
                  folgend={
                    darfBuchen && z.kind !== 'correction' && !storniert.has(z.id) ? (
                      <Button
                        variant="text"
                        onClick={() => {
                          const betrag = centsMitVorzeichen(z.amountCents, konto.currency)
                          void bestaetige({
                            titel: 'Buchung stornieren?',
                            text: `Buchung über ${betrag} stornieren?`,
                            bestaetigen: 'Stornieren',
                            gefahr: true,
                          }).then((bestaetigt) => {
                            if (bestaetigt) stornieren.mutate(z.id)
                          })
                        }}
                      >
                        Stornieren
                      </Button>
                    ) : undefined
                  }
                />
              ))}
            </ul>
          )}
        </section>
      ) : null}

      <Sheet offen={blattOffen} titel="Buchen" onSchliessen={() => setBlattOffen(false)}>
        <form
          className="stack-4"
          onSubmit={(e) => {
            e.preventDefault()
            buchen.mutate()
          }}
        >
          <Select
            id="buchung-art"
            label="Art"
            wert={formular.kind}
            onWert={(w) => setFormular((f) => ({ ...f, kind: w as NeueBuchung['kind'] }))}
          >
            <option value="allowance">Taschengeld</option>
            <option value="bonus">Bonus</option>
            <option value="spend">Ausgabe</option>
          </Select>
          <TextField
            id="buchung-betrag"
            label="Betrag in Euro"
            wert={formular.betrag}
            onWert={(w) => setFormular((f) => ({ ...f, betrag: w }))}
            inputMode="decimal"
            {...(formular.kind === 'spend' ? { hilfe: 'Wird als Ausgabe abgezogen.' } : {})}
          />
          <TextField
            id="buchung-datum"
            label="Datum"
            type="date"
            wert={formular.occurredOn}
            onWert={(w) => setFormular((f) => ({ ...f, occurredOn: w }))}
          />
          <TextField
            id="buchung-zweck"
            label="Zweck (optional)"
            wert={formular.note}
            onWert={(w) => setFormular((f) => ({ ...f, note: w }))}
          />
          {/* ⚠️ Frei über den Knöpfen, nicht an einem Feld: der Server nennt
              kein Feld, also darf die Oberfläche auch keines behaupten. Zuvor
              hing die Meldung fest am E-Mail-Feld - eine Meldung über die
              Telefonnummer markierte damit die E-Mail als ungültig. */}
          {fehler ? (
            <p className="fehler" role="alert">
              {fehler}
            </p>
          ) : null}
          <div className="blatt__fuss-inline">
            <Button variant="text" onClick={() => setBlattOffen(false)}>
              Abbrechen
            </Button>
            <Button type="submit" laeuft={buchen.isPending}>
              Buchen
            </Button>
          </div>
        </form>
      </Sheet>
    </>
  )
}
